import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AdAccountSettings = {
  id: string;
  user_id: string;
  ad_account_id: string;
  default_fb_page_id: string | null;
  default_fb_page_name: string | null;
  default_ig_account_id: string | null;
  default_ig_account_name: string | null;
  default_pixel_id: string | null;
  default_pixel_name: string | null;
  default_app_id: string | null;
  default_app_name: string | null;
  default_ad_status: string | null;
  website_events_enabled: boolean | null;
  app_events_enabled: boolean | null;
  offline_events_enabled: boolean | null;
  crm_events_enabled: boolean | null;
  post_engagements_enabled: boolean | null;
  page_engagements_enabled: boolean | null;
  naming_convention: any;
  default_utm_parameters: string | null;
  image_enhancements: any;
  video_enhancements: any;
  carousel_enhancements?: Record<string, boolean> | null;
  collection_enhancements?: Record<string, "OPT_IN" | "OPT_OUT"> | null;
  enhancements_catalog: any;
  flexible_enhancements?: Record<string, boolean> | null;
  flexible_advantage_plus_creative?: boolean;
  sitelinks?: any;
  multi_advertiser_opt_in: boolean | null;
  onboarding_completed: boolean | null;
  onboarding_completed_at: string | null;
  step_profiles_completed: boolean;
  step_tracking_completed: boolean;
  step_naming_completed: boolean;
  step_launch_completed: boolean;
  step_enhancements_completed: boolean;
  created_at: string | null;
  updated_at: string | null;
};

export function useOnboardingSettings(adAccountId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["onboarding-settings", user?.id, adAccountId];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<AdAccountSettings> => {
      if (!user?.id || !adAccountId) throw new Error("Missing user or ad account");

      // Try to load existing settings
      const { data, error } = await supabase
        .from("ad_account_settings")
        .select("*")
        .eq("user_id", user.id)
        .eq("ad_account_id", adAccountId)
        .maybeSingle();

      if (error) throw error;

      if (data) return data as AdAccountSettings;

      // Create empty row with defaults
      const { data: newRow, error: insertError } = await supabase
        .from("ad_account_settings")
        .insert({
          user_id: user.id,
          ad_account_id: adAccountId,
        })
        .select()
        .single();

      if (insertError) throw insertError;
      return newRow as AdAccountSettings;
    },
    enabled: !!user?.id && !!adAccountId,
    staleTime: 30 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: async (stepData: Partial<AdAccountSettings>) => {
      if (!user?.id || !adAccountId) throw new Error("Missing user or ad account");

      const { data, error } = await supabase
        .from("ad_account_settings")
        .upsert(
          {
            user_id: user.id,
            ad_account_id: adAccountId,
            ...stepData,
          },
          { onConflict: "user_id,ad_account_id" }
        )
        .select()
        .single();

      if (error) throw error;
      return data as AdAccountSettings;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !adAccountId) throw new Error("Missing user or ad account");

      const { data, error } = await supabase
        .from("ad_account_settings")
        .update({
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString(),
        })
        .eq("user_id", user.id)
        .eq("ad_account_id", adAccountId)
        .select()
        .single();

      if (error) throw error;
      return data as AdAccountSettings;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });

  return {
    settings: query.data ?? null,
    isLoading: query.isLoading,
    saveStep: saveMutation.mutateAsync,
    completeOnboarding: completeMutation.mutateAsync,
    isSaving: saveMutation.isPending || completeMutation.isPending,
  };
}
